import { useEffect, useState, useCallback } from 'react';
import { api, ApiError } from '../lib/api';
import {
  MAX_SUGGEST_PER_PERSON,
  type HostEvent,
  type ManageInvitees,
  type FeedbackFlag,
  type MovieOption,
} from '../../shared/protocol';
import {
  eventLink,
  hostLink,
  formatWhen,
  recurrenceText,
  copyToClipboard,
  placeToRoomCode,
} from '../lib/share';
import { ShareRow } from './ShareRow';
import { GuidesRow, PcJoinButton } from './Guides';
import { TallyBoard } from './TallyBoard';
import { EditDetails } from './EditDetails';
import { EditMovies } from './EditMovies';
import { AiImageReview } from './AiImageReview';
import { BakingBar } from './BakingBar';
import { PosterLightbox } from './PosterLightbox';
import { OvenChip } from './OvenChip';

/**
 * The host's command center after creating an event: the general magic link +
 * multi-channel share, every invitee's personal link + suggestion allowance, the
 * live vote tally (polled), and a reminder to bookmark the private host link.
 */
export function HostDashboard({ initial, token }: { initial: HostEvent; token: string }) {
  const [event, setEvent] = useState<HostEvent>(initial);
  const [copied, setCopied] = useState<string | null>(null);
  const [editing, setEditing] = useState<null | 'details' | 'movies'>(null);
  const [inviteeDraft, setInviteeDraft] = useState('');
  const [inviteeError, setInviteeError] = useState<string | null>(null);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<MovieOption | null>(null);

  const refresh = useCallback(async () => {
    try {
      const ev = await api.getHostEvent(event.id, token);
      setEvent(ev);
    } catch {
      // transient — keep showing last good state
    }
  }, [event.id, token]);

  // live tally: poll every 15s (the host wants fresher data than voters, but 5s
  // was burning Cloudflare free-tier requests). Paused while editing so a refresh
  // can't clobber an open form, and paused while the tab is hidden — coming back
  // to the tab refreshes immediately so the host never stares at a stale tally.
  useEffect(() => {
    if (editing) return;
    let t: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (t === null) t = setInterval(refresh, 15000);
    };
    const stop = () => {
      if (t !== null) {
        clearInterval(t);
        t = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        refresh(); // catch up right away after being backgrounded
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh, editing]);

  const generalLink = eventLink(event.id);
  const privateHostLink = hostLink(event.id, token);
  const recur = recurrenceText(event.recurrence);

  const flashCopy = async (key: string, text: string) => {
    await copyToClipboard(text);
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 1600);
  };

  // host invitee mutations (add / remove / set suggestion allowance)
  const mutateInvitees = useCallback(
    async (body: ManageInvitees) => {
      setInviteeError(null);
      try {
        setEvent(await api.manageInvitees(event.id, token, body));
      } catch (e) {
        setInviteeError(e instanceof ApiError ? e.message : 'Could not update the invitees.');
      }
    },
    [event.id, token],
  );

  const addPeople = () => {
    const names = inviteeDraft
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean);
    if (names.length === 0) return;
    mutateInvitees({ v: 1, op: 'add', names });
    setInviteeDraft('');
  };

  const setSuggest = (code: string, limit: number) =>
    mutateInvitees({
      v: 1,
      op: 'setSuggest',
      code,
      limit: Math.max(0, Math.min(MAX_SUGGEST_PER_PERSON, limit)),
    });

  const removeInvitee = (code: string, name: string) => {
    if (window.confirm(`Remove ${name}? Their personal link stops working and their vote is dropped.`)) {
      mutateInvitees({ v: 1, op: 'remove', code });
    }
  };

  // parody-poster review/regenerate (host auths with the host token). Both endpoints
  // return the public view, so we refresh() to pull the full host view afterward.
  const regeneratePoster = async (optionId: string, feedback: FeedbackFlag[], notes: string) => {
    setReviewError(null);
    try {
      await api.regeneratePoster(event.id, { v: 1, optionId, feedback, notes }, token);
      await refresh(); // art now 'baking'; the 5s poll swaps in the new poster
    } catch (e) {
      setReviewError(e instanceof ApiError ? e.message : 'Could not regenerate the poster.');
    }
  };
  const approvePoster = async (optionId: string) => {
    setReviewError(null);
    try {
      await api.approveArt(event.id, { v: 1, optionId }, token);
      setReviewId(null);
      await refresh();
    } catch (e) {
      setReviewError(e instanceof ApiError ? e.message : 'Could not approve the poster.');
    }
  };

  // Poster Review Time: queue the MC roast-audio bake for the VR theater's
  // pre-movie mini-game (the same local oven that bakes the parody posters)
  const [showBakeMsg, setShowBakeMsg] = useState<string | null>(null);
  const [showBaking, setShowBaking] = useState(false);
  const bakePosterShow = async () => {
    setShowBaking(true);
    setShowBakeMsg(null);
    try {
      await api.bakeReviewShow(event.id, token);
      setShowBakeMsg('🎤 Queued! The oven writes + voices the MC intros on the box — ready in the VR theater in a few minutes.');
    } catch (e) {
      setShowBakeMsg(e instanceof ApiError ? e.message : 'Could not queue the poster show.');
    } finally {
      setShowBaking(false);
    }
  };

  // made-up movies (anything the poster oven is handling)
  const posterOptions = event.options.filter((o) => o.art);

  if (editing === 'details') {
    return (
      <div>
        <EditDetails
          event={event}
          token={token}
          onSaved={(ev) => {
            setEvent(ev);
            setEditing(null);
          }}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  if (editing === 'movies') {
    return (
      <div>
        <EditMovies
          event={event}
          token={token}
          onSaved={(ev) => {
            setEvent(ev);
            setEditing(null);
          }}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="dash-cols">
      <div className="card marquee bulbs">
        <div className="flex-between">
          <h2 className="display">{event.title}</h2>
          <span className="chip">🍿 it's on!</span>
        </div>
        <p className="soft">
          {formatWhen(event.startsAt)}
          {recur ? ` · ${recur}` : ''} · <strong>{event.place}</strong>
        </p>
        {event.note && <p className="muted">{event.note}</p>}
        {(() => {
          const code = placeToRoomCode(event.place);
          return code ? <PcJoinButton code={code} /> : null;
        })()}

        <div className="pill-row">
          <button className="btn ghost small" onClick={() => setEditing('details')}>
            ✏️ Edit details
          </button>
          <button className="btn ghost small" onClick={() => setEditing('movies')}>
            🎬 Edit movies
          </button>
          <button className="btn ghost small" onClick={bakePosterShow} disabled={showBaking}>
            {showBaking ? '⏳ Queueing…' : '🎤 Bake poster show'}
          </button>
        </div>
        {showBakeMsg && <p className="muted">{showBakeMsg}</p>}

        <div className="notice warn">
          <strong>Bookmark your host link</strong> to come back and watch votes roll in. It's the only
          way back to this dashboard — keep it private.
          <div className="codebox" style={{ marginTop: 8 }}>
            {privateHostLink}
          </div>
          <button
            className="btn ghost small"
            style={{ marginTop: 8 }}
            onClick={() => flashCopy('host', privateHostLink)}
          >
            {copied === 'host' ? 'Copied!' : 'Copy host link'}
          </button>
        </div>
      </div>

      <div className="card">
        <h2>📣 Invite the crew</h2>
        <p className="soft">One link for everyone — vote + join from here.</p>
        <ShareRow
          link={generalLink}
          parts={{
            title: event.title,
            startsAt: event.startsAt,
            place: event.place,
            recurrence: event.recurrence,
            note: event.note,
            link: generalLink,
          }}
        />
        {event.hasDiscordWebhook && (
          <div className="notice success" style={{ marginTop: 12 }}>
            🎮 Posted to your Discord channel — and it'll ping there as votes arrive.
          </div>
        )}
      </div>

      <div className="card">
        <h2>👥 People &amp; suggestions</h2>
        <p className="soft">
          Each person gets their own link — sending these (instead of the general link) lets the
          tally show exactly who's voted. Use 💡 to let someone suggest movies (and cap how many).
        </p>

        {event.invitees.length > 0 && (
          <div className="invitee-list">
            {event.invitees.map((inv) => {
              const link = eventLink(event.id, inv.code);
              return (
                <div className="invitee-row" key={inv.code}>
                  <span className="name">{inv.name}</span>
                  <span className={`badge${inv.voted ? ' voted' : ''}`}>
                    {inv.voted ? '✓ voted' : 'not yet'}
                  </span>
                  <div
                    className="suggest-ctl"
                    title="Movies this person may suggest (0 = can't suggest)"
                  >
                    <span className="ic">💡</span>
                    <button
                      className="step"
                      onClick={() => setSuggest(inv.code, inv.suggestLimit - 1)}
                      disabled={inv.suggestLimit <= 0}
                      aria-label={`Fewer suggestions for ${inv.name}`}
                    >
                      −
                    </button>
                    <span className="suggest-count">
                      {inv.suggestLimit === 0
                        ? "can't suggest"
                        : `${inv.suggestionsUsed}/${inv.suggestLimit}`}
                    </span>
                    <button
                      className="step"
                      onClick={() => setSuggest(inv.code, inv.suggestLimit + 1)}
                      disabled={inv.suggestLimit >= MAX_SUGGEST_PER_PERSON}
                      aria-label={`More suggestions for ${inv.name}`}
                    >
                      +
                    </button>
                  </div>
                  <button className="btn ghost small" onClick={() => flashCopy(inv.code, link)}>
                    {copied === inv.code ? 'Copied!' : 'Copy link'}
                  </button>
                  <button
                    className="remove-x"
                    onClick={() => removeInvitee(inv.code, inv.name)}
                    title={`Remove ${inv.name}`}
                    aria-label={`Remove ${inv.name}`}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <label style={{ marginTop: 16 }}>Add people</label>
        <div className="row">
          <input
            type="text"
            placeholder="Friend's name (or a few, comma-separated)"
            value={inviteeDraft}
            onChange={(e) => setInviteeDraft(e.target.value)}
            onKeyDown={(e) => (e.key === 'Enter' ? (e.preventDefault(), addPeople()) : null)}
            maxLength={120}
            style={{ flex: '2 1 200px' }}
          />
          <button className="btn ghost" onClick={addPeople} style={{ flex: '0 0 auto' }}>
            Add
          </button>
        </div>
        {inviteeError && <div className="notice error">{inviteeError}</div>}
      </div>

      {posterOptions.length > 0 && (
        <div className="card">
          <div className="flex-between">
            <h2>🎨 Made-up movie posters</h2>
            <OvenChip />
          </div>
          <p className="soft">
            No match was found for these, so the poster oven invents one. Review to approve or
            regenerate with notes. Posters bake while your box + ComfyUI are running.
          </p>
          <div className="poster-list">
            {posterOptions.map((o) => {
              const baking = o.art === 'pending' || o.art === 'baking';
              const status = baking
                ? 'baking…'
                : o.art === 'ready'
                  ? o.artApproved
                    ? '✓ approved'
                    : 'ready — review it'
                  : o.art === 'blocked'
                    ? 'skipped (content)'
                    : 'bake failed — try regenerate';
              return (
                <div className="poster-row" key={o.id}>
                  <div
                    className={`poster-thumb${baking ? ' baking' : ''}`}
                    style={{
                      ...(o.posterUrl ? { backgroundImage: `url(${o.posterUrl})` } : {}),
                      cursor: o.posterUrl ? 'zoom-in' : undefined,
                    }}
                    onClick={o.posterUrl ? () => setLightbox(o) : undefined}
                    role={o.posterUrl ? 'button' : undefined}
                    title={o.posterUrl ? 'View poster' : undefined}
                  >
                    {!o.posterUrl && (baking ? '🎨' : '🎬')}
                  </div>
                  <div className="poster-info">
                    <div className="t">{o.title}</div>
                    {baking ? <BakingBar /> : <div className="soft">{status}</div>}
                    {!baking && o.poster?.tagline && (
                      <div className="tagline">“{o.poster.tagline}”</div>
                    )}
                  </div>
                  <button
                    className="btn ghost small"
                    onClick={() => {
                      setReviewError(null);
                      setReviewId(o.id);
                    }}
                  >
                    🎨 Review
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex-between">
          <h2>🗳️ Live results</h2>
          <span className="chip">auto-updates</span>
        </div>
        <TallyBoard event={event} onPosterClick={setLightbox} />
      </div>
      </div>

      <h3 className="section-heading display">How your crew joins</h3>
      <GuidesRow roomCode={placeToRoomCode(event.place) ?? undefined} />

      {lightbox && <PosterLightbox option={lightbox} onClose={() => setLightbox(null)} />}

      {reviewId &&
        (() => {
          const opt = event.options.find((o) => o.id === reviewId);
          if (!opt) return null;
          return (
            <AiImageReview
              option={opt}
              busy={opt.art === 'baking' || opt.art === 'pending'}
              error={reviewError}
              onApprove={() => approvePoster(opt.id)}
              onRegenerate={(feedback, notes) => regeneratePoster(opt.id, feedback, notes)}
              onClose={() => setReviewId(null)}
            />
          );
        })()}
    </div>
  );
}
